import { Link } from 'react-router';
import { useApi } from '../lib/api';
import { dateTime, money, titleCase } from '../lib/format';
import { useSession } from '../lib/session';
import type { Anomaly, ChainVerification, ControlResult, Controls } from '../lib/types';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Empty, PageHeader, Query, Stat, StatusBadge, type Tone } from '../components/ui';

const CATEGORIES: ControlResult['category'][] = ['Ledger', 'Subledger', 'Cash', 'Close', 'Governance'];
const SEVERITY: Record<Anomaly['severity'], Tone> = { HIGH: 'bad', MEDIUM: 'warn', LOW: 'neutral' };

export function ControlsPage() {
  const controls = useApi<Controls>('/api/controls');
  const chain = useApi<ChainVerification>('/api/ledger/verify');
  const anomalies = useApi<Anomaly[]>('/api/anomalies');
  const refreshing = controls.isFetching || chain.isFetching || anomalies.isFetching;
  return (
    <>
      <PageHeader
        title="Controls"
        description="Continuous controls run against the live ledger: sub-ledgers tie to the GL, cash reconciles to the bank, the hash chain is intact, and approvals were respected. Anomaly detection flags entries a reviewer should look at."
        actions={
          <Button
            icon={<Icons.Refresh size={15} />}
            loading={refreshing}
            onClick={() => {
              void controls.refetch();
              void chain.refetch();
              void anomalies.refetch();
            }}
          >
            Run now
          </Button>
        }
      />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <Query q={controls}>
            {(r) => (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Passing" value={String(r.summary.PASS)} tone="good" sub={`Ran ${dateTime(r.ranAt)}`} />
                  <Stat label="Warnings" value={String(r.summary.WARN)} tone={r.summary.WARN ? 'warn' : undefined} />
                  <Stat label="Failing" value={String(r.summary.FAIL)} tone={r.summary.FAIL ? 'bad' : 'good'} />
                </div>
                {CATEGORIES.map((cat) => {
                  const rows = r.results.filter((x) => x.category === cat);
                  if (!rows.length) return null;
                  return (
                    <Card key={cat} title={cat} pad={false}>
                      <ul className="divide-y divide-line">
                        {rows.map((x) => (
                          <ControlRow key={x.key} r={x} />
                        ))}
                      </ul>
                    </Card>
                  );
                })}
              </>
            )}
          </Query>
        </div>
        <div className="space-y-5">
          <ChainCard q={chain} />
          <AnomaliesCard q={anomalies} />
        </div>
      </div>
    </>
  );
}

function ControlRow({ r }: { r: ControlResult }) {
  const icon = r.status === 'PASS' ? <Icons.CheckCircle size={18} /> : <Icons.Alert size={18} />;
  const { currency } = useSession();
  const metrics = Object.entries(r.metrics ?? {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object' && typeof v !== 'boolean');
  return (
    <li className="flex gap-3 px-4 py-3">
      <span className={cx('mt-0.5 shrink-0', r.status === 'PASS' ? 'text-good' : r.status === 'WARN' ? 'text-warn' : 'text-bad')}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium">{r.name}</span>
          <StatusBadge status={r.status} />
        </div>
        <p className="mt-0.5 text-[12.5px] text-muted">{r.detail}</p>
        {metrics.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px] text-faint">
            {metrics.slice(0, 5).map(([k, v]) => (
              <span key={k}>
                {titleCase(k.replace(/([a-z])([A-Z])/g, '$1_$2'))}: <span className="num text-muted">{metric(k, v, currency)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

const MONEY_KEY = /amount|balance|base|total|tolerance|difference|variance|gross|net|open/i;

function metric(key: string, v: unknown, currency: string) {
  if (typeof v === 'number') return MONEY_KEY.test(key) ? money(v, currency, { cents: false }) : v.toLocaleString();
  const s = String(v);
  return s.length > 20 ? `${s.slice(0, 12)}…` : s;
}

function ChainCard({ q }: { q: { data?: ChainVerification; error: unknown; isLoading: boolean } }) {
  return (
    <Card title="Ledger integrity" subtitle="Each posted entry is hashed with the one before it.">
      <Query q={q}>
        {(c) => (
          <div>
            <div className={cx('flex items-center gap-3 rounded-lg p-3', c.ok ? 'bg-good-soft' : 'bg-bad-soft')}>
              <span className={c.ok ? 'text-good' : 'text-bad'}>{c.ok ? <Icons.Lock size={22} /> : <Icons.Unlock size={22} />}</span>
              <div>
                <div className={cx('text-[14px] font-semibold', c.ok ? 'text-good' : 'text-bad')}>{c.ok ? 'Chain verified' : 'Chain broken'}</div>
                <div className="text-[12px] text-ink-2">{c.checked.toLocaleString()} posted entries recomputed from genesis</div>
              </div>
            </div>
            {c.brokenAt && (
              <p className="mt-3 text-[12.5px] text-bad">
                Break at sequence {c.brokenAt.chainSeq}:{' '}
                <Link to={`/journal/${c.brokenAt.entryId}`} className="underline">
                  JE-{c.brokenAt.number}
                </Link>{' '}
                — {c.brokenAt.reason}
              </p>
            )}
            <div className="mt-3 space-y-1 text-[11.5px]">
              <div className="flex justify-between gap-3">
                <span className="text-muted">Head</span>
                <code className="truncate text-ink-2">{c.head.slice(0, 24)}…</code>
              </div>
              {!c.ok && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted">Expected</span>
                  <code className="truncate text-ink-2">{c.expectedHead.slice(0, 24)}…</code>
                </div>
              )}
            </div>
          </div>
        )}
      </Query>
    </Card>
  );
}

function AnomaliesCard({ q }: { q: { data?: Anomaly[]; error: unknown; isLoading: boolean } }) {
  const { currency } = useSession();
  return (
    <Card title="Anomalies" subtitle="Last 90 days" pad={false}>
      <Query q={q}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty title="Nothing unusual" icon={<Icons.CheckCircle size={20} />}>
              No duplicate bills, outliers or risky manual entries.
            </Empty>
          ) : (
            <ul className="divide-y divide-line">
              {rows.slice(0, 30).map((a) => (
                <li key={a.id}>
                  <Link to={a.entityType === 'bill' ? `/ap/bills/${a.entityId}` : `/journal/${a.entityId}`} className="block px-4 py-3 hover:bg-paper">
                    <div className="flex items-center gap-2">
                      <Badge tone={SEVERITY[a.severity]}>{titleCase(a.severity)}</Badge>
                      <span className="truncate text-[13px] font-medium">{a.title}</span>
                      {a.amount !== undefined && <span className="num ml-auto text-[12.5px] text-ink-2">{money(a.amount, a.currency ?? currency)}</span>}
                    </div>
                    <p className="mt-1 text-[12px] text-muted">{a.detail}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </Query>
    </Card>
  );
}
